// ── Adapter Supabase do IActivitiesRepository ─────────────────────
// Implementa a porta do módulo Activity usando o cliente Supabase. É a ÚNICA
// camada que conhece as tabelas/RPCs do banco para Activities.

import { getSupabase, type TypedSupabaseClient } from '@infra/supabase/client';
import type { ActivityEntry } from '@/types/activity';
import type { IActivitiesRepository, TeamMemberRow } from '@modules/activity';

const requireSupabase = (): TypedSupabaseClient => {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase nao inicializado.');
  return supabase;
};

const SELECT_RICH = `
  *,
  lead:leads!activities_lead_id_fkey (
    id, nome, telefone, email
  ),
  card:cards!activities_card_id_fkey (
    id, stage_id, pipeline_id, sdr_id, closer_id,
    pipeline_stages ( id, name, color ),
    pipelines ( id, name, role, funnel_id )
  ),
  assignee:profiles!activities_assigned_to_fkey (
    id, nome, initials, color, avatar_url
  )
`;

// Teto de linhas do PostgREST. Uma consulta sem `range` é CORTADA aqui, em
// silêncio: sem erro, sem aviso, sem indicação de que faltou coisa.
const PENDING_PAGE = 1000;
// Trava de segurança para a paginação nunca virar laço infinito. 50k atividades
// pendentes já é muito além do que a Central consegue exibir.
const PENDING_MAX_PAGES = 50;

export class SupabaseActivitiesRepository implements IActivitiesRepository {
  // Paginado de propósito. A cadência automática cria uma atividade `pending`
  // por card novo, então o volume acompanha o de leads: o TechFinance já estava
  // em 918 das 1000 vagas. Passar do teto não dá erro — a Central de Atividades
  // simplesmente para de mostrar as vencidas mais antigas, que são exatamente as
  // que importam. Mesma classe de falha que esvaziava o Kanban
  // (ver SupabaseLeadRepository.listLeadsGraph).
  listPendingActivities: IActivitiesRepository['listPendingActivities'] = async ({ workspaceId, type = null }) => {
    const supabase = requireSupabase();
    const rows: ActivityEntry[] = [];

    for (let page = 0; page < PENDING_MAX_PAGES; page++) {
      let query = supabase
        .from('activities')
        .select(SELECT_RICH)
        .eq('workspace_id', workspaceId)
        .eq('status', 'pending')
        .order('due_date', { ascending: true })
        // Desempate obrigatório: a cadência cria centenas de atividades com o
        // mesmo `due_date`. Sem uma segunda chave a ordem entre elas não é
        // estável e a paginação repetiria umas e pularia outras.
        .order('id', { ascending: true })
        .range(page * PENDING_PAGE, page * PENDING_PAGE + PENDING_PAGE - 1);

      if (type) query = query.eq('type', type);

      const { data, error } = await query;
      if (error) throw error;

      const batch = (data || []) as unknown as ActivityEntry[];
      rows.push(...batch);
      if (batch.length < PENDING_PAGE) break;

      // A trava existe para não virar laço infinito, mas se algum dia ela
      // morder ela recria o corte silencioso que esta paginação veio consertar
      // — só num teto mais alto. Então ela avisa.
      if (page === PENDING_MAX_PAGES - 1) {
        console.warn(
          `[SupabaseActivitiesRepository] listPendingActivities parou no teto de ${PENDING_MAX_PAGES} páginas ` +
          `(${PENDING_MAX_PAGES * PENDING_PAGE} atividades). A lista está incompleta.`,
        );
      }
    }

    return rows;
  };

  listTeamMembers: IActivitiesRepository['listTeamMembers'] = async (workspaceId) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('time_members')
      .select(`
        user_id, role,
        profiles!time_members_user_id_fkey ( id, nome, initials, color, avatar_url ),
        times!inner ( workspace_id )
      `)
      .in('role', ['sdr', 'closer'])
      .eq('ativo', true)
      .eq('times.workspace_id', workspaceId);
    if (error) return [];
    return (data || []) as unknown as TeamMemberRow[];
  };

  listByLead: IActivitiesRepository['listByLead'] = async (leadId) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('activities')
      .select(`*, assignee:profiles!activities_assigned_to_fkey ( id, nome, initials, color )`)
      .eq('lead_id', leadId)
      .order('due_date', { ascending: true });
    if (error) return [];
    return (data || []) as unknown as ActivityEntry[];
  };

  insertActivity: IActivitiesRepository['insertActivity'] = async ({
    workspaceId, cardId, leadId, assignedTo,
    type, title, description = '', script = '', dueDate,
    remindBeforeMinutes = null,
  }) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('activities')
      .insert({
        workspace_id: workspaceId,
        card_id:      cardId,
        lead_id:      leadId,
        assigned_to:  assignedTo || null,
        type, title, description, script,
        due_date:     dueDate,
        status:       'pending',
        // Config/103 lê esta coluna para materializar os lembretes.
        remind_before_minutes: remindBeforeMinutes,
      })
      .select(SELECT_RICH)
      .single();
    if (error) throw error;
    return data as unknown as ActivityEntry;
  };

  markActivityDone: IActivitiesRepository['markActivityDone'] = async (id) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('activities')
      .update({ status: 'done', completed_at: new Date().toISOString() })
      .eq('id', id)
      .select(SELECT_RICH)
      .single();
    if (error) throw error;
    return data as unknown as ActivityEntry;
  };

  insertHistory: IActivitiesRepository['insertHistory'] = async (entry) => {
    const supabase = requireSupabase();
    const { error } = await supabase.from('history').insert(entry as never);
    if (error) throw error;
  };

  listPendingActivityIdsForCard: IActivitiesRepository['listPendingActivityIdsForCard'] = async (cardId) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('activities')
      .select('id')
      .eq('card_id', cardId)
      .eq('status', 'pending');
    if (error) throw error;
    return (data || []).map(a => a.id);
  };

  cancelActivitiesByIds: IActivitiesRepository['cancelActivitiesByIds'] = async (ids) => {
    const supabase = requireSupabase();
    const { error } = await supabase
      .from('activities')
      .update({ status: 'cancelled' })
      .in('id', ids);
    if (error) throw error;
  };

  cancelActivity: IActivitiesRepository['cancelActivity'] = async (id) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('activities')
      .update({ status: 'cancelled' })
      .eq('id', id)
      .select(SELECT_RICH)
      .single();
    if (error) throw error;
    return data as unknown as ActivityEntry;
  };

  reopenActivity: IActivitiesRepository['reopenActivity'] = async (id) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('activities')
      // completed_at volta a null: o Config/103 remonta os lembretes ao ver
      // status='pending' de novo, e a Central filtra por completed_at.
      .update({ status: 'pending', completed_at: null })
      .eq('id', id)
      .select(SELECT_RICH)
      .single();
    if (error) throw error;
    return data as unknown as ActivityEntry;
  };

  getActivityDueDate: IActivitiesRepository['getActivityDueDate'] = async (id) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('activities')
      .select('due_date')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data.due_date as string;
  };

  updateActivityDueDate: IActivitiesRepository['updateActivityDueDate'] = async (id, dueDateIso) => {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from('activities')
      .update({ due_date: dueDateIso })
      .eq('id', id)
      .select(SELECT_RICH)
      .single();
    if (error) throw error;
    return data as unknown as ActivityEntry;
  };
}
